<template>
    <div class="flex flex-column align-items-center min-h-screen w-full pt-6">

        <!-- Loading packages -->
        <div v-if="packagesLoading" class="flex align-items-center justify-content-center py-8 gap-3">
            <ProgressSpinner style="width: 40px; height: 40px" />
            <span class="text-600">Loading packages...</span>
        </div>

        <TutorSubscriptionPlans
            v-else
            mode="onboarding"
            :packages="tutorPackages"
            :featureDefinitions="tutorFeatures"
            :disableActions="actionLoading"
            :currentPlanId="packageActivated ? selectedPackage?.id : null"
            @package-action="handlePackageAction"
        />

        <!-- Billing Details Dialog -->
        <BillingDetailsDialog
            v-model="showBillingDialog"
            :context="billingContext"
            :selected-package="selectedPackage"
            :initial-values="billingInitialValues"
            @submit="submitBilling"
        />

        <!-- Free Plan Confirmation Dialog -->
        <Dialog
            v-model:visible="showFreePlanDialog"
            modal
            :showHeader="false"
            :style="{ width: 'min(480px, 95vw)', borderRadius: '16px', overflow: 'hidden', padding: '0' }"
            class="p-fluid"
        >
            <div class="fp-header">
                <div class="fp-header-left">
                    <div class="fp-icon-wrap">
                        <i class="pi pi-tag fp-icon"></i>
                    </div>
                    <div>
                        <p class="fp-title">Activate Free Plan</p>
                        <p class="fp-subtitle">No payment required</p>
                    </div>
                </div>
            </div>

            <div class="fp-body">
                <div class="fp-banner">
                    <i class="pi pi-check-circle fp-banner-icon"></i>
                    <div>
                        <p class="fp-banner-heading">{{ selectedPackage?.name }} — Completely Free</p>
                        <p class="fp-banner-body">
                            No card details or payment needed.
                            Your plan activates instantly and you're ready to go.
                        </p>
                    </div>
                </div>
                <div class="fp-info-list">
                    <div class="fp-info-row">
                        <span class="fp-dot"></span>
                        <span>Activates immediately upon confirmation</span>
                    </div>
                    <div class="fp-info-row">
                        <span class="fp-dot"></span>
                        <span>You can upgrade to a paid plan at any time</span>
                    </div>
                </div>
            </div>

            <template #footer>
                <div class="fp-footer">
                    <Button label="Cancel" class="fp-btn-cancel" @click="showFreePlanDialog = false" />
                    <Button label="Activate Plan" icon="pi pi-check" class="fp-btn-activate" @click="confirmFreePlan" />
                </div>
            </template>
        </Dialog>

        <!-- Progress Dialog -->
        <Dialog
            v-model:visible="showProgressDialog"
            modal
            :closable="false"
            header="Setting up your subscription"
            :style="{ width: 'min(420px, 92vw)' }"
        >
            <div class="flex flex-column align-items-center py-4 gap-4">
                <ProgressSpinner style="width: 50px; height: 50px" />
                <p class="text-600 text-center m-0">{{ progressMessage }}</p>
            </div>
        </Dialog>

        <!-- Bottom navigation -->
        <div class="flex justify-content-between mt-4 mb-2 px-4" style="max-width: 1100px; width: 100%;">
            <Button label="Back" class="teal-button-transparent" @click="emit('previous-step')" />
            <Button
                label="Next"
                class="teal-button"
                :disabled="!packageActivated"
                @click="emit('package-selected', { ...selectedPackage })"
            />
        </div>

    </div>
</template>

<script setup>
import { ref, computed, onMounted, defineEmits } from 'vue';
import TutorSubscriptionPlans from '@/components/subscriptionPlans/tutorSubscriptionPlans.vue';
import BillingDetailsDialog from '@/components/payments/BillingDetailsForm.vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import { useAuthStore } from '@/stores/authStore';
import payments from '@/api/payments';
import subscriptionApi from '@/api/subscription';
import { updateProfile } from '@/api/tutor';
import { backendApi } from '@/api/axios';
import { PACKAGES_ENDPOINTS, PAYMENT_ENDPOINTS } from '@/api/endpoints';
import { notifyError, notifyContrast } from '@/utils/notification';
import { notifyIfManagedByInstitute } from '@/utils/subscriptionGuards';

// sessionStorage key used to resume subscription after PayHere redirect
const PENDING_KEY = 'tutorOnboardingPendingPackage';

const emit = defineEmits(['package-selected', 'previous-step']);
const authStore = useAuthStore();

// UI state 
const packagesLoading = ref(true);
const actionLoading   = ref(false);

const showBillingDialog   = ref(false);
const showFreePlanDialog  = ref(false);
// 'paid_subscribe' → paid plan, no card: actual subscription amount shown in dialog
const billingContext = ref('paid_subscribe');

const showProgressDialog = ref(false);
const progressMessage    = ref('');
const packageActivated   = ref(false);

// Data 
const tutorPackages   = ref([]);
const selectedPackage = ref(null);

// Pre-fill billing form from the user's saved profile
const billingInitialValues = computed(() => {
    const u = authStore.user;
    if (!u) return {};
    return {
        firstName: u.firstName    || '',
        lastName:  u.lastName     || '',
        email:     u.email        || '',
        phone:     u.mobileNumber || '',
        address:   u.address      || '',
        city:      u.city         || '',
    };
});

// Feature definitions 
const tutorFeatures = ref([
    { key: 'listedSubjects',  label: 'Listed subjects' },
    { key: 'enrolledStudents',label: 'Enrolled students' },
    { key: 'notes',           label: 'Notes' },
    { key: 'inAppChat',       label: 'In app chat' },
    { key: 'securePayments',  label: 'Secure payments' },
    { key: 'aiFeatures',      label: 'AI features', info: 'Get access to our AI learning assistant' },
    { key: 'pastPapers',      label: 'Past papers' },
    { key: 'sessions',        label: 'Sessions via platform (per month)' },
]);

// Package mapping 
function mapStringFeature(value) {
    if (value === null || value === false || value === 'none') return false;
    // 'all' means the feature is fully included - show a tick rather than the word "All".
    if (value === true || value === 'all') return true;
    if (typeof value === 'string') return value.charAt(0).toUpperCase() + value.slice(1);
    return value;
}

function mapLimitValue(value) {
    return value === null ? 'Unlimited' : value.toString();
}

function mapApiPackage(pkg) {
    return {
        id:          pkg._id,
        name:        pkg.name,
        priceLabel:  pkg.pricing.amount === 0 ? '0' : pkg.pricing.amount.toString(),
        popular:     pkg.isPopular,
        pricingType: pkg.pricing.type,
        features: {
            listedSubjects:   mapLimitValue(pkg.limits.listedSubjects),
            enrolledStudents: mapLimitValue(pkg.limits.enrolledStudents),
            notes:            mapLimitValue(pkg.limits.notes),
            sessions:         mapLimitValue(pkg.limits.sessionsPerMonth),
            securePayments:   pkg.features.securePayments,
            pastPapers:       pkg.features.pastPapers,
            inAppChat:        pkg.features.inAppChat,
            aiFeatures:       mapStringFeature(pkg.features.aiFeatures),
        },
    };
}

// Helpers 
function markActivated(pkg) {
    if (pkg) selectedPackage.value = pkg;
    packageActivated.value = true;
}

// packageId is a populated object in the API response: { _id, name, ... }
function findPackageFromSub(sub) {
    const rawId = sub.packageId?._id ?? sub.packageId;
    if (!rawId) return null;
    return tutorPackages.value.find(p => String(p.id) === String(rawId)) ?? null;
}

// API helpers 
async function fetchPackages() {
    try {
        const res = await backendApi.get(PACKAGES_ENDPOINTS.GET_ALL, { params: { userType: 'tutor' } });
        tutorPackages.value = (res.data?.data || []).map(mapApiPackage);
    } catch {
        notifyError('Error', 'Failed to load packages. Please refresh the page.');
    }
}

async function checkHasToken() {
    const res = await payments.getMyToken();
    return res?.data?.data?.hasToken === true;
}

// After subscribing, some flows require polling until the subscription becomes active
async function waitForSubscriptionActive(maxAttempts = 15, intervalMs = 2000) {
    for (let i = 0; i < maxAttempts; i++) {
        try {
            const res = await subscriptionApi.getMy();
            if (res?.data?.data?.subscription?.status === 'active') return true;
        } catch { /* ignore transient errors */ }
        await new Promise(r => setTimeout(r, intervalMs));
    }
    return false;
}

function redirectToPayHere(paymentData, preapprove = false) {
    const isSandbox = paymentData.sandbox === 'true' || paymentData.sandbox === true;
    const url = preapprove
        ? (isSandbox ? PAYMENT_ENDPOINTS.PAYHERE_SANDBOX_PREAPPROVE_URL : PAYMENT_ENDPOINTS.PAYHERE_LIVE_PREAPPROVE_URL)
        : (isSandbox ? PAYMENT_ENDPOINTS.PAYHERE_SANDBOX_URL : PAYMENT_ENDPOINTS.PAYHERE_LIVE_URL);
    payments.processPayHereRedirect({ ...paymentData, action: url }, {});
}

// Core subscribe handler — autoAdvance: emit package-selected on success (false for free plan dialog flow)
async function doSubscribe(packageId, billingData, autoAdvance = true) {
    progressMessage.value    = 'Setting up your subscription...';
    showProgressDialog.value = true;
    try {
        const payload = { packageId, ...(billingData || {}) };
        const res  = await subscriptionApi.subscribe(payload);
        const data = res?.data?.data;

        // Paid plan, no card: redirect to PayHere checkout for payment and card saving
        if (data?.requiresCheckout) {
            showProgressDialog.value = false;
            sessionStorage.setItem(PENDING_KEY, JSON.stringify({ packageId, pricingType: 'paid', ts: Date.now() }));
            redirectToPayHere(data.checkoutData, true);
            return;
        }

        // Free plan, no card: redirect to PayHere tokenization flow to save card and do LKR 30 charge for verification
        if (data?.requiresTokenization) {
            showProgressDialog.value = false;
            sessionStorage.setItem(PENDING_KEY, JSON.stringify({ packageId, pricingType: 'free', ts: Date.now() }));
            redirectToPayHere(data.tokenizationData, true);
            return;
        }

        // Subscription activated directly (e.g. free plan — no payment required)
        if (data?.subscription) {
            showProgressDialog.value = false;
            sessionStorage.removeItem(PENDING_KEY);
            markActivated(selectedPackage.value);
            notifyContrast('Success', 'Your subscription is now active!');
            if (autoAdvance) emit('package-selected', { ...selectedPackage.value });
            return;
        }

        // Subscription is active immediately
        if (data?.status === 'active') {
            showProgressDialog.value = false;
            sessionStorage.removeItem(PENDING_KEY);
            markActivated(selectedPackage.value);
            notifyContrast('Success', 'Your subscription is now active!');
            if (autoAdvance) emit('package-selected', { ...selectedPackage.value });
            return;
        }

        // Pending activation — poll until active
        progressMessage.value = 'Waiting for subscription to activate...';
        const activated = await waitForSubscriptionActive();
        showProgressDialog.value = false;
        if (activated) {
            sessionStorage.removeItem(PENDING_KEY);
            markActivated(selectedPackage.value);
            notifyContrast('Success', 'Your subscription is now active!');
            if (autoAdvance) emit('package-selected', { ...selectedPackage.value });
        } else {
            notifyError('Timeout', 'Subscription activation is taking longer than expected. Please refresh and try again.');
        }
    } catch (err) {
        showProgressDialog.value = false;
        if (notifyIfManagedByInstitute(err)) return;
        const msg = err?.response?.data?.message || '';
        if (msg.toLowerCase().includes('already have an active subscription')) {
            await resolveExistingSubscription();
        } else {
            notifyError('Error', msg || 'Failed to subscribe. Please try again.');
        }
    }
}

// Handle plan button click
async function handlePackageAction(event) {
    selectedPackage.value = event.package;
    actionLoading.value   = true;
    try {
        // Free plan: show confirmation dialog — activation stays on this page, user clicks Next
        if (selectedPackage.value.pricingType === 'free') {
            showFreePlanDialog.value = true;
            return;
        }

        const hasToken = await checkHasToken();
        if (hasToken) {
            // Card on file — subscribe directly, no billing form needed
            await doSubscribe(selectedPackage.value.id, null);
        } else {
            // No card, paid plan — show billing form
            billingContext.value    = 'paid_subscribe';
            showBillingDialog.value = true;
        }
    } catch {
        notifyError('Error', 'Something went wrong. Please try again.');
    } finally {
        actionLoading.value = false;
    }
}

// Confirm free plan — activate but stay on this page so user can click Next themselves
async function confirmFreePlan() {
    showFreePlanDialog.value = false;
    await doSubscribe(selectedPackage.value.id, null, false);
}

// Submit billing → save to profile, then subscribe
async function submitBilling(formData) {
    showBillingDialog.value = false;

    // Save billing details to user profile
    try {
        await updateProfile({
            firstName:    formData.firstName,
            lastName:     formData.lastName,
            mobileNumber: formData.phone,
            address:      formData.address,
            city:         formData.city,
        });
        if (authStore.user) {
            authStore.user.firstName    = formData.firstName;
            authStore.user.lastName     = formData.lastName;
            authStore.user.mobileNumber = formData.phone;
            authStore.user.address      = formData.address;
            authStore.user.city         = formData.city;
        }
    } catch {
        /* ignore */
    }

    await doSubscribe(selectedPackage.value.id, formData);
}

// On mount: resume pending subscription if returning from PayHere success
async function checkPendingSubscription() {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return;

    let pending;
    try { pending = JSON.parse(raw); } catch {
        sessionStorage.removeItem(PENDING_KEY);
        return;
    }

    if (!pending?.packageId) {
        sessionStorage.removeItem(PENDING_KEY);
        return;
    }

    // If we reach here with PENDING_KEY present but subscription not active, the payment was
    // cancelled or "Back to site" was used. Clean up backend and let user try again — no popup.
    sessionStorage.removeItem(PENDING_KEY);
    subscriptionApi.cancelPending().catch(() => {});
}

// Fetch active subscription
async function resolveExistingSubscription() {
    try {
        const res = await subscriptionApi.getMy();
        const sub = res?.data?.data?.subscription;
        if (sub?.status === 'active') {
            const pkg = findPackageFromSub(sub);
            markActivated(pkg);
            notifyContrast('Subscription Active', 'You already have an active subscription.');
            emit('package-selected', pkg ? { ...pkg } : {});
        }
    } catch { /* silent — page stays, user can try again */ }
}

onMounted(async () => {
    packagesLoading.value = true;
    try {
        await fetchPackages();

        try {
            const res = await subscriptionApi.getMy();
            const sub = res?.data?.data?.subscription;
            if (sub?.status === 'active') {
                markActivated(findPackageFromSub(sub));
                return;
            }
        } catch { /* no subscription yet */ }

        await checkPendingSubscription();
    } finally {
        packagesLoading.value = false;
    }
});
</script>

<style scoped>
/* Free plan confirmation dialog — mirrors BillingDetailsForm structure */

.fp-header {
    padding: 1.1rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
}

.fp-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.fp-icon-wrap {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: #DCFCE7;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.fp-icon {
    font-size: 15px;
    color: #25c560;
}

.fp-title {
    font-size: 15px;
    font-weight: 600;
    margin: 0;
    color: #111827;
}

.fp-subtitle {
    font-size: 12px;
    color: #6B7280;
    margin: 0;
}

.fp-body {
    padding: 1.25rem 1.5rem;
}

.fp-banner {
    border: 1px solid #BBF7D0;
    background: #F0FDF4;
    border-radius: 8px;
    padding: 0.875rem 1rem;
    margin-bottom: 1rem;
    display: flex;
    gap: 10px;
    align-items: flex-start;
}

.fp-banner-icon {
    color: #16A34A;
    font-size: 15px;
    margin-top: 1px;
    flex-shrink: 0;
}

.fp-banner-heading {
    font-size: 13px;
    font-weight: 600;
    margin: 0 0 3px;
    color: #15803D;
}

.fp-banner-body {
    font-size: 12px;
    margin: 0;
    color: #16A34A;
    line-height: 1.65;
}

.fp-info-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.fp-info-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #374151;
}

.fp-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #13be85;
    flex-shrink: 0;
}

.fp-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 0.25rem 0;
}

.fp-btn-cancel {
    font-size: 13px;
    padding: 8px 16px;
    border-radius: 8px;
    background: transparent !important;
    border: 1px solid #D1D5DB !important;
    color: #374151 !important;
}

.fp-btn-activate {
    font-size: 13px;
    font-weight: 600;
    padding: 8px 18px;
    border-radius: 8px;
    border: none !important;
    color: #fff !important;
    background: #13be85 !important;
}

.fp-btn-activate:hover:not(:disabled) {
    background: #0fa872 !important;
}
</style>
